import { useEffect, useState } from "react";
import Term, {
  definitionMaxSize,
  getGroupString,
  memoMaxSize,
  pronunciationMaxSize,
  termMaxSize,
} from "../Term";
import styles from "../css/TermEditScreen.module.css";
import cloneDeep from "lodash/cloneDeep";
import PlaceHolderTextField from "../../common/PlaceHolderTextField";
import GroupSelectScreen from "../../groups/GroupSelectScreen";
import { useTranslation } from "react-i18next";
import Group from "../../groups/Group";
import { useReadingSettings } from "../../common/util/chineseCharacterMode";
import DropDownButton from "../../common/view/DropDownButton";
import ContainerNavigationView from "../../common/view/ContainerNavigationView";
import { ReactComponent as CloseIcon } from "../../../img/iconImgAllClose.svg";
import { useBackToClose } from "../../../hooks/useBackToClose";

interface TermEditScreenProps {
  open: boolean;
  term: Term;
  handleOnSave: (term: Term) => void;
  handleOnClose: () => void;
}

export default function TermEditScreen(props: TermEditScreenProps) {
  useBackToClose(props.open, props.handleOnClose);
  const { t } = useTranslation();
  const { chineseCharacterMode } = useReadingSettings();

  const [termToSave, setTermToSave] = useState<Term>();
  const [showGroupSelect, setShowGroupSelect] = useState(false);

  useEffect(() => {
    setTermToSave(cloneDeep(props.term));
  }, [props.term]);

  function handleOnClickBackground() {
    props.handleOnClose();
  }

  function handleOnClickTermEditContainer(
    event: React.MouseEvent<HTMLDivElement>
  ) {
    event.stopPropagation();
  }

  function handleOnClickCancelButton() {
    props.handleOnClose();
  }

  function handleOnClickSaveButton() {
    if (termToSave !== undefined && termToSave !== null) {
      props.handleOnSave(termToSave);
    }
    props.handleOnClose();
  }

  function handleOnClickGroupButton() {
    setShowGroupSelect(true);
  }

  function handleTermValueChange(value: string) {
    const newTerm = cloneDeep(termToSave);
    if (newTerm !== undefined) {
      newTerm.term = value;
      setTermToSave(newTerm);
    }
  }

  function handleDefinitionValueChange(value: string) {
    const newTerm = cloneDeep(termToSave);
    if (newTerm !== undefined) {
      newTerm.definition = value;
      setTermToSave(newTerm);
    }
  }

  function handleMemoValueChange(value: string) {
    const newTerm = cloneDeep(termToSave);
    if (newTerm !== undefined) {
      newTerm.memo = value;
      setTermToSave(newTerm);
    }
  }

  function handlePronunciationValueChange(value: string) {
    const newTerm = cloneDeep(termToSave);
    if (newTerm !== undefined) {
      newTerm.pronunciation = value;
      setTermToSave(newTerm);
    }
  }

  function updateGroup(group: string) {
    const newTerm = cloneDeep(termToSave);
    if (newTerm !== undefined) {
      newTerm.group = group;
      setTermToSave(newTerm);
    }
  }

  if (props.open === true) {
    return (
      <>
        <div className={styles.stack}>
          <div
            className={styles.container}
            onClick={() => handleOnClickBackground()}
          >
            <div
              className={styles.termEditContainer}
              onClick={handleOnClickTermEditContainer}
            >
              <div
                className={styles.contentContainer}
                lang={
                  chineseCharacterMode === "japanese"
                    ? "ja"
                    : navigator?.language
                }
              >
                <ContainerNavigationView
                  open={true}
                  leftImageButtonImage={CloseIcon}
                  onClickLeftImageButton={handleOnClickCancelButton}
                  titleView={
                    <div className={styles.groupContainer}>
                      <DropDownButton
                        title={getGroupString(termToSave?.group ?? "")}
                        handleOnClick={handleOnClickGroupButton}
                      />
                    </div>
                  }
                  rightTextButtonText={t("Save")}
                  rightTextButtonColor="var(--text-accent-color)"
                  onClickRightTextButton={handleOnClickSaveButton}
                />
                <div className={styles.contentTitleAndContentContainer}>
                  <span className={styles.contentTitle}>{t("Word")}</span>
                  <PlaceHolderTextField
                    text={termToSave?.term}
                    maxCount={termMaxSize}
                    lineStyle="multiple"
                    handleTextChange={handleTermValueChange}
                  />
                </div>

                <div className={styles.contentTitleAndContentContainer}>
                  <span className={styles.contentTitle}>{t("Meaning")}</span>
                  <PlaceHolderTextField
                    text={termToSave?.definition}
                    maxCount={definitionMaxSize}
                    lineStyle="multiple"
                    handleTextChange={handleDefinitionValueChange}
                  />
                </div>

                <div className={styles.contentTitleAndContentContainer}>
                  <span className={styles.contentTitle}>
                    {t("Description")}
                  </span>
                  <PlaceHolderTextField
                    text={termToSave?.memo}
                    maxCount={memoMaxSize}
                    lineStyle="multiple"
                    handleTextChange={handleMemoValueChange}
                  />
                </div>

                <div className={styles.contentTitleAndContentContainer}>
                  <span className={styles.contentTitle}>
                    {t("Pronunciation")}
                  </span>
                  <PlaceHolderTextField
                    text={termToSave?.pronunciation}
                    maxCount={pronunciationMaxSize}
                    lineStyle="multiple"
                    handleTextChange={handlePronunciationValueChange}
                  />
                </div>
              </div>
            </div>
          </div>

          <GroupSelectScreen
            open={showGroupSelect === true}
            selectStyle="single"
            showAllGroup={false}
            showNoGroup={true}
            showGroupAddButton={true}
            selectedGroups={
              termToSave === undefined || termToSave === null
                ? undefined
                : [termToSave.group]
            }
            handleOnSelectGroups={(groups: Group[]) => {
              if (groups.length !== 0) {
                const group = groups[0];
                updateGroup(group.isUnspecifiedGroup ? "" : group.title);
              }
            }}
            handleOnClose={() => {
              setShowGroupSelect(false);
            }}
          />
        </div>
      </>
    );
  } else {
    return null;
  }
}
